import { useState } from "react";
import { Link } from "react-router-dom";
import { Stethoscope, UserRound, MessageCircleHeart, Star, BookOpen, MapPin, CalendarHeart, Video, Search, Smile, Timer, Bug, Apple, Sparkles, Trophy } from "lucide-react";
import { InteractiveHero } from "@/components/home/InteractiveHero";
import { Reviews } from "@/components/home/Reviews";
import { ToothMascot } from "@/components/site/ToothMascot";

const GUIDE = {
  parents: [
    { to: "/book", icon: CalendarHeart, t: "Book a clinic visit", d: "Pick a service, date and free time slot in under a minute.", bg: "bg-[#fff1f1]" },
    { to: "/book?type=online", icon: Video, t: "Online consultation", d: "Video call with the doctor. The clinic emails you the link.", bg: "bg-[#e0f2fe]" },
    { to: "/my-appointments", icon: Search, t: "Check my bookings", d: "Type your phone number or email to see your visits. No login needed.", bg: "bg-[#ebfbfa]" },
    { to: "/ask", icon: MessageCircleHeart, t: "Ask Sparkle (AI)", d: "Guideline-based answers 24/7. Signed-in chats are remembered.", bg: "bg-[#f3f0ff]" },
    { to: "/services", icon: Stethoscope, t: "Services", d: "Check-ups, fillings, root canals, crowns, braces, special-needs care.", bg: "bg-[#fef7e5]" },
    { to: "/about", icon: UserRound, t: "Meet Dr. Anuj", d: "Pediatric dentist and our playroom-style clinic.", bg: "bg-[#ebfbfa]" },
    { to: "/blog", icon: BookOpen, t: "Parent tips", d: "Short guides on brushing, snacks and first visits.", bg: "bg-[#e0f2fe]" },
    { to: "/reviews", icon: Star, t: "Reviews & FAQ", d: "5.0 on Google from 36 families, plus answers to common questions.", bg: "bg-[#fff1f1]" },
    { to: "/contact", icon: MapPin, t: "Visit us", d: "Marve Rd, Malad West. Mon–Sat, 10–1 and 5–9.", bg: "bg-[#fef7e5]" },
  ],
  kids: [
    { to: "/#top", icon: Smile, t: "Tooth Explorer", d: "Tap the teeth at the top of this page to meet each one!", bg: "bg-[#ebfbfa]" },
    { to: "/fun-zone?game=brush", icon: Timer, t: "Brushing Challenge", d: "Brush along for 2 minutes and collect stars.", bg: "bg-[#fef7e5]" },
    { to: "/fun-zone?game=bugs", icon: Bug, t: "Sugar Bug Smash", d: "Tap away cavity-causing bugs. Don't hit the apples!", bg: "bg-[#fff1f1]" },
    { to: "/fun-zone?game=snacks", icon: Apple, t: "Snack Sorter", d: "Find out which snacks are Tooth Friends.", bg: "bg-[#e0f2fe]" },
    { to: "/fun-zone?game=scrub", icon: Sparkles, t: "Scrub-a-Tooth", d: "Rub off the plaque and learn how to brush the right way.", bg: "bg-[#f3f0ff]" },
    { to: "/fun-zone?game=quiz", icon: Trophy, t: "Tooth Hero Quiz", d: "Answer 5 questions to become a Tooth Hero.", bg: "bg-[#ebfbfa]" },
  ],
};

const Explore = () => {
  const [tab, setTab] = useState("parents");
  return (
  <section className="mx-auto max-w-7xl px-6 py-20 lg:px-8" data-testid="home-explore">
    <span className="text-xs font-bold uppercase tracking-widest text-teal-600">Your guide to this website</span>
    <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
      <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">What's inside Tooth Buds</h2>
      <div className="inline-flex rounded-full border-2 border-slate-800 bg-white p-1" data-testid="guide-tabs">
        {[["parents", "For parents"], ["kids", "For kids"]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`guide-tab-${v}`}
            className={`rounded-full px-5 py-2 text-sm font-bold transition-colors ${tab === v ? "bg-teal-600 text-white" : "text-slate-600"}`}>{l}</button>
        ))}
      </div>
    </div>
    <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {GUIDE[tab].map((e, i) => (
        <Link key={e.t} to={e.to} data-testid={`guide-${tab}-${i}`}
          className={`btn-pop group rounded-[1.75rem] p-7 ${e.bg}`}>
          <e.icon className="h-8 w-8 text-teal-600 transition-transform group-hover:rotate-12" />
          <p className="font-display mt-4 text-xl font-semibold text-slate-800">{e.t} →</p>
          <p className="mt-1 text-sm text-slate-600">{e.d}</p>
        </Link>
      ))}
    </div>
  </section>
  );
};

const FunTeaser = () => (
  <section className="mx-auto max-w-7xl px-6 lg:px-8" data-testid="funzone-teaser">
    <Link to="/fun-zone" className="group relative flex flex-col items-center gap-8 overflow-hidden rounded-[2.5rem] bg-[#f5b82e] p-10 sm:flex-row sm:p-14">
      <ToothMascot mood="wow" className="w-32 shrink-0 transition-transform duration-500 group-hover:rotate-12 group-hover:scale-110" />
      <div className="flex-1 text-center sm:text-left">
        <p className="font-display text-3xl font-bold text-slate-900 sm:text-4xl">Hey kids! Ready for the 2-minute brushing challenge?</p>
        <p className="mt-2 font-semibold text-slate-800">Brush along with Sparkle, collect stars and take the Tooth Hero quiz.</p>
      </div>
      <span className="btn-pop rounded-full bg-slate-900 px-7 py-4 font-bold text-white">Play now →</span>
    </Link>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <InteractiveHero />
      <Explore />
      <FunTeaser />
      <Reviews />
    </div>
  );
}
